import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, TrendingUp, Users, Repeat, MousePointer2, Smartphone, Monitor, Tablet, RefreshCw } from "lucide-react";
import { adminClient } from "@/lib/apiClient";

const PERIOD_OPTIONS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "all", label: "All time" },
];

const DEVICE_ICONS = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
};

function KpiTile({ Icon, label, value, sublabel, testId }) {
  return (
    <div className="bg-white rounded border border-gray-200 p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">{label}</div>
        <Icon size={16} className="text-br-bronze" />
      </div>
      <div className="text-3xl font-bold tabular-nums text-gray-900">{value}</div>
      {sublabel && <div className="text-xs text-gray-500 mt-1">{sublabel}</div>}
    </div>
  );
}

function Timeseries({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-gray-500">No traffic recorded in this period yet.</div>
    );
  }
  const maxViews = Math.max(...data.map((d) => d.views), 1);
  return (
    <div className="p-5">
      <div className="flex items-end gap-1 h-40" data-testid="timeseries-chart">
        {data.map((d) => {
          const pct = Math.max(4, Math.round((d.views / maxViews) * 100));
          return (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end group relative min-w-0">
              <div
                className="w-full bg-br-bronze rounded-t hover:bg-br-bronze-2 transition-colors"
                style={{ height: `${pct}%` }}
              />
              <div className="absolute -top-7 hidden group-hover:block bg-br-black text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                {d.day}: {d.views} views · {d.unique_visitors} visitors
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] uppercase tracking-widest text-gray-500">
        <span>{data[0]?.day}</span>
        <span>{data[data.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function TopPagesList({ items }) {
  if (!items || items.length === 0) {
    return <div className="p-6 text-center text-sm text-gray-500">No page data yet.</div>;
  }
  const maxViews = Math.max(...items.map((i) => i.views), 1);
  return (
    <ul className="divide-y divide-gray-100" data-testid="top-pages-list">
      {items.map((p) => {
        const pct = Math.round((p.views / maxViews) * 100);
        return (
          <li key={p.path} className="p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono truncate text-gray-800" title={p.path}>{p.path}</div>
              <div className="mt-1 h-1 bg-gray-100 rounded overflow-hidden">
                <div className="h-full bg-br-bronze" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold tabular-nums">{p.views}</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">{p.unique_visitors} uniq</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TopReferrersList({ items }) {
  if (!items || items.length === 0) {
    return <div className="p-6 text-center text-sm text-gray-500">No external referrers yet — most traffic is direct so far.</div>;
  }
  return (
    <ul className="divide-y divide-gray-100" data-testid="top-referrers-list">
      {items.map((r) => (
        <li key={r.referrer} className="p-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs truncate text-gray-800" title={r.referrer}>{r.referrer || "(direct)"}</div>
          </div>
          <div className="text-sm font-bold tabular-nums">{r.views}</div>
        </li>
      ))}
    </ul>
  );
}

function DeviceBreakdown({ items }) {
  if (!items || items.length === 0) {
    return <div className="p-6 text-center text-sm text-gray-500">No device data yet.</div>;
  }
  const total = items.reduce((a, b) => a + b.views, 0) || 1;
  return (
    <div className="p-3 space-y-2" data-testid="device-breakdown">
      {items.map((d) => {
        const Icon = DEVICE_ICONS[d.device_type] || Monitor;
        const pct = Math.round((d.views / total) * 100);
        return (
          <div key={d.device_type} className="flex items-center gap-3 px-3 py-2">
            <Icon size={16} className="text-br-bronze" />
            <div className="text-xs uppercase tracking-widest w-20 text-gray-700">{d.device_type}</div>
            <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
              <div className="h-full bg-br-bronze" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs tabular-nums w-16 text-right">{d.views} · {pct}%</div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (p) => {
    setLoading(true); setError("");
    try {
      const res = await adminClient.get(`/admin/analytics/summary?period=${p}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load analytics");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  const totals = data?.totals || {};
  const repeatPct = useMemo(() => {
    if (!totals.unique_visitors) return 0;
    return Math.round((totals.repeat_visitors / totals.unique_visitors) * 100);
  }, [totals.unique_visitors, totals.repeat_visitors]);

  return (
    <div className="space-y-5" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs uppercase tracking-widest text-gray-500 mr-2">Period</div>
        {PERIOD_OPTIONS.map((opt) => {
          const isActive = period === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => setPeriod(opt.key)}
              data-testid={`analytics-period-${opt.key}`}
              aria-pressed={isActive}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-br-bronze ${
                isActive
                  ? "bg-br-black text-white border-br-black"
                  : "bg-white text-gray-700 border-gray-200 hover:border-br-bronze"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => load(period)}
          className="ml-auto text-xs uppercase tracking-widest px-3 py-1.5 rounded border border-gray-200 bg-white hover:border-br-bronze flex items-center gap-1"
          data-testid="analytics-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading && (
        <div className="p-10 flex items-center justify-center text-gray-500 bg-white border border-gray-200 rounded">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading analytics...
        </div>
      )}

      {!loading && error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiTile
              Icon={TrendingUp}
              label="Page Views"
              value={totals.page_views ?? 0}
              sublabel={`${totals.sessions ?? 0} sessions`}
              testId="kpi-pageviews"
            />
            <KpiTile
              Icon={Users}
              label="Unique Visitors"
              value={totals.unique_visitors ?? 0}
              sublabel={`${totals.new_visitors ?? 0} new`}
              testId="kpi-unique"
            />
            <KpiTile
              Icon={Repeat}
              label="Repeat Visitors"
              value={totals.repeat_visitors ?? 0}
              sublabel={`${repeatPct}% return rate`}
              testId="kpi-repeat"
            />
            <KpiTile
              Icon={MousePointer2}
              label="Pages / Session"
              value={totals.pages_per_session ?? 0}
              sublabel={`engagement depth`}
              testId="kpi-pages-per-session"
            />
          </div>

          <div className="bg-white rounded border border-gray-200">
            <div className="px-5 pt-4 pb-1 flex items-center justify-between">
              <div className="font-display tracking-widest text-sm uppercase text-gray-800">Traffic Over Time</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Daily page views</div>
            </div>
            <Timeseries data={data.timeseries} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded border border-gray-200">
              <div className="px-5 pt-4 pb-2 font-display tracking-widest text-sm uppercase text-gray-800">Top Pages</div>
              <TopPagesList items={data.top_pages} />
            </div>
            <div className="bg-white rounded border border-gray-200">
              <div className="px-5 pt-4 pb-2 font-display tracking-widest text-sm uppercase text-gray-800">Top Referrers</div>
              <TopReferrersList items={data.top_referrers} />
            </div>
          </div>

          <div className="bg-white rounded border border-gray-200">
            <div className="px-5 pt-4 pb-2 font-display tracking-widest text-sm uppercase text-gray-800">Device Breakdown</div>
            <DeviceBreakdown items={data.devices} />
          </div>
        </>
      )}
    </div>
  );
}
